import { useEffect, useMemo, useRef, useState } from 'react';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Area, LinePath } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { curveMonotoneX } from '@visx/curve';
import { dollars, longDate, marketPosition, movingAverage, ordinal, parseDay, positionZone } from './format';

// The chart palette is dark-only: the pricing board always renders on the wallboard-style dark panels.
export const colors = {
  price: '#f4f7fb',
  live: '#37d39a',
  bandLow: '#8ea0bd',
  bandMid: '#5b8cff',
  bandHigh: '#7ea4ff',
  medianBooked: '#ffbd5c',
  event: '#9b7bff',
  booked: '#8ea0bd',
  market: '#ffbd5c',
  override: '#ff6f86',
  yours: '#5b8cff',
  position: '#4dd0e1',
  axis: '#8c9ab0',
  grid: '#263449',
};

// Measures an element so the SVG is drawn at its real pixel size and stays crisp inside the scaled TV stage.
export function useSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = () => setSize({ width: Math.floor(node.clientWidth), height: Math.floor(node.clientHeight) });
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

const axisText = (size) => ({ fill: colors.axis, fontSize: size, fontFamily: "'DM Sans', sans-serif" });

function monthTicks(rows) {
  if (!rows.length) return [];
  const first = new Date(rows[0].t);
  const last = new Date(rows[rows.length - 1].t);
  const span = (last - first) / 86400000;
  const ticks = [];
  if (span <= 45) {
    rows.forEach((row, index) => { if (index % 7 === 0) ticks.push(new Date(row.t)); });
    return ticks;
  }
  for (let date = new Date(first.getFullYear(), first.getMonth() + 1, 1, 12); date <= last; date = new Date(date.getFullYear(), date.getMonth() + 1, 1, 12)) ticks.push(date);
  return ticks;
}

const tickLabel = (rows) => {
  const span = rows.length > 1 ? (rows[rows.length - 1].t - rows[0].t) / 86400000 : 0;
  return (date) => new Intl.DateTimeFormat('en-US', span <= 45 ? { month: 'short', day: 'numeric' } : { month: 'short' }).format(date);
};

function eventSpans(rows) {
  const spans = [];
  rows.forEach((row, index) => {
    if (!row.event) return;
    const last = spans[spans.length - 1];
    if (last && last.name === row.event && last.endIndex === index - 1) last.endIndex = index;
    else spans.push({ name: row.event, startIndex: index, endIndex: index });
  });
  return spans;
}

function nearestIndex(rows, x, scale) {
  const time = scale.invert(x).getTime();
  let best = 0;
  rows.forEach((row, index) => { if (Math.abs(row.t - time) < Math.abs(rows[best].t - time)) best = index; });
  return best;
}

function demandTone(demand) {
  return { Low: '#ff6f86', Normal: '#8ea0bd', Good: '#37d39a', High: '#ffbd5c' }[demand] || colors.axis;
}

// The two stacked charts from the PriceLabs "How do my prices and demand compare to the market" view:
// your price over the market percentile bands (with holidays and overrides), and your booked nights against
// market occupancy. Hover details are for the hub; the wallboard shows the static version.
export function PriceMarketCharts({ pricing, bookedDates, rangeDays = 90, interactive = false, showPosition = false }) {
  const [wrapRef, size] = useSize();
  const [hover, setHover] = useState(null);

  const rows = useMemo(() => {
    const marketByDate = new Map();
    (pricing.market?.dates || []).forEach((date, index) => marketByDate.set(date, {
      p25: pricing.market.p25[index], p50: pricing.market.p50[index], p75: pricing.market.p75[index],
      p90: pricing.market.p90[index], medianBooked: pricing.market.medianBooked[index],
    }));
    const occupancyByDate = new Map();
    (pricing.marketOccupancy?.dates || []).forEach((date, index) => occupancyByDate.set(date, {
      market: pricing.marketOccupancy.occupancy[index], lastYear: pricing.marketOccupancy.sameTimeLastYear[index],
    }));
    const overrideByDate = new Map((pricing.overrides || []).map((item) => [item.date, item]));
    return pricing.days.slice(0, rangeDays).map((day) => ({
      ...day,
      t: parseDay(day.date).getTime(),
      isBooked: day.booked || bookedDates.has(day.date),
      position: marketPosition(day.price, marketByDate.get(day.date)),
      market: marketByDate.get(day.date) || null,
      marketOccupancy: occupancyByDate.get(day.date) || null,
      override: overrideByDate.get(day.date) || null,
    }));
  }, [pricing, bookedDates, rangeDays]);

  const legendHeight = 18;
  const gap = 4;
  const footerHeight = 16 + (showPosition ? 20 : 0);
  const total = Math.max(0, size.height - legendHeight - footerHeight);
  const body = Math.max(0, total - gap * (showPosition ? 2 : 1));
  const priceHeight = Math.round(body * (showPosition ? 0.5 : 0.66));
  const occupancyHeight = showPosition ? Math.round(body * 0.26) : Math.max(0, body - priceHeight);
  const positionHeight = showPosition ? Math.max(0, body - priceHeight - occupancyHeight) : 0;
  const margin = { top: 10, right: 8, bottom: 19, left: 34 };
  const innerWidth = Math.max(0, size.width - margin.left - margin.right);
  const compact = size.width < 700;
  const fontSize = compact ? 8 : 10;

  const x = useMemo(() => scaleTime({
    domain: rows.length ? [new Date(rows[0].t), new Date(rows[rows.length - 1].t)] : [new Date(), new Date()],
    range: [0, innerWidth],
  }), [rows, innerWidth]);
  const maxPrice = Math.max(100, ...rows.flatMap((row) => [row.price, row.livePrice, row.market?.p90, row.market?.p75]).filter(Number.isFinite));
  const innerPriceHeight = Math.max(0, priceHeight - margin.top - margin.bottom);
  const yPrice = scaleLinear({ domain: [0, Math.ceil((maxPrice * 1.05) / 50) * 50], range: [innerPriceHeight, 0] });
  const innerOccupancyHeight = Math.max(0, occupancyHeight - margin.top - margin.bottom);
  const yOccupancy = scaleLinear({ domain: [0, 100], range: [innerOccupancyHeight, 0] });
  const innerPositionHeight = Math.max(0, positionHeight - margin.top - margin.bottom);
  const yPosition = scaleLinear({ domain: [0, 100], range: [innerPositionHeight, 0] });
  // How the open nights spread across the market's price quartiles.
  const zones = useMemo(() => {
    const open = rows.filter((row) => !row.isBooked && row.position !== null);
    const counts = [0, 0, 0, 0];
    open.forEach((row) => { counts[positionZone(row.position)] += 1; });
    return { counts, total: open.length, average: open.length ? open.reduce((sum, row) => sum + row.position, 0) / open.length : null };
  }, [rows]);
  const barWidth = rows.length ? Math.max(1, innerWidth / rows.length - 0.5) : 1;
  const spans = useMemo(() => eventSpans(rows), [rows]);
  const ticks = useMemo(() => monthTicks(rows), [rows]);
  const labelFor = useMemo(() => tickLabel(rows), [rows]);
  const hovered = interactive && hover !== null ? rows[hover] : null;

  function overlay(height) {
    if (!interactive) return null;
    return (
      <rect
        x={0} y={0} width={innerWidth} height={height} fill="transparent"
        onPointerMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          setHover(nearestIndex(rows, ((event.clientX - box.left) / box.width) * innerWidth, x));
        }}
        onPointerLeave={() => setHover(null)}
      />
    );
  }

  const cursor = hovered ? <line x1={x(new Date(hovered.t))} x2={x(new Date(hovered.t))} y1={0} y2={Math.max(innerPriceHeight, innerOccupancyHeight, innerPositionHeight)} stroke={colors.axis} strokeDasharray="2 3" strokeOpacity=".7" pointerEvents="none" /> : null;

  return (
    <div className="pb-charts" ref={wrapRef}>
      <div className="pb-legend" style={{ height: legendHeight }}>
        <span><i className="swatch line" style={{ background: colors.price }} />Your price</span>
        <span><i className="swatch dash" style={{ borderColor: colors.live }} />Live on Hospitable</span>
        <span><i className="swatch band" style={{ background: colors.bandLow, opacity: 0.5 }} />Market 25–50</span>
        <span><i className="swatch band" style={{ background: colors.bandMid, opacity: 0.6 }} />50–75</span>
        <span><i className="swatch band" style={{ background: colors.bandHigh, opacity: 0.3 }} />75–90</span>
        <span><i className="swatch dot" style={{ background: colors.override }} />Override</span>
        <span><i className="swatch band" style={{ background: colors.event }} />Holiday / event</span>
      </div>
      {size.width > 0 && size.height > 0 && (
        <>
          <svg width={size.width} height={priceHeight} role="img" aria-label="Your price against the market price range">
            <g transform={`translate(${margin.left},${margin.top})`}>
              <GridRows scale={yPrice} width={innerWidth} numTicks={4} stroke={colors.grid} strokeOpacity={0.7} />
              <Area data={rows} defined={(d) => d.market} x={(d) => x(new Date(d.t))} y0={(d) => yPrice(d.market.p75)} y1={(d) => yPrice(d.market.p90)} curve={curveMonotoneX} fill={colors.bandHigh} fillOpacity={0.16} />
              <Area data={rows} defined={(d) => d.market} x={(d) => x(new Date(d.t))} y0={(d) => yPrice(d.market.p50)} y1={(d) => yPrice(d.market.p75)} curve={curveMonotoneX} fill={colors.bandMid} fillOpacity={0.36} />
              <Area data={rows} defined={(d) => d.market} x={(d) => x(new Date(d.t))} y0={(d) => yPrice(d.market.p25)} y1={(d) => yPrice(d.market.p50)} curve={curveMonotoneX} fill={colors.bandLow} fillOpacity={0.3} />
              <LinePath data={rows} defined={(d) => d.market && Number.isFinite(d.market.medianBooked)} x={(d) => x(new Date(d.t))} y={(d) => yPrice(d.market.medianBooked)} curve={curveMonotoneX} stroke={colors.medianBooked} strokeOpacity={0.75} strokeWidth={1} strokeDasharray="1 3" />
              <LinePath data={rows} defined={(d) => Number.isFinite(d.livePrice)} x={(d) => x(new Date(d.t))} y={(d) => yPrice(d.livePrice)} curve={curveMonotoneX} stroke={colors.live} strokeWidth={1.25} strokeDasharray="4 3" />
              <LinePath data={rows} defined={(d) => Number.isFinite(d.price)} x={(d) => x(new Date(d.t))} y={(d) => yPrice(d.price)} curve={curveMonotoneX} stroke={colors.price} strokeWidth={compact ? 1.5 : 2} />
              {rows.filter((row) => row.override).map((row) => (
                <circle key={row.date} cx={x(new Date(row.t))} cy={yPrice(row.price ?? 0)} r={compact ? 3 : 4} fill={colors.override} stroke="#101827" strokeWidth={1.5} />
              ))}
              {spans.map((span) => {
                const left = x(new Date(rows[span.startIndex].t)) - barWidth / 2;
                const width = x(new Date(rows[span.endIndex].t)) - x(new Date(rows[span.startIndex].t)) + barWidth;
                return (
                  <g key={`${span.name}-${span.startIndex}`}>
                    <rect x={left} y={innerPriceHeight - 5} width={width} height={5} rx={1.5} fill={colors.event} />
                    {width >= 46 && <text x={left + width / 2} y={innerPriceHeight - 8} textAnchor="middle" fill="#c4b5ff" fontSize={compact ? 7 : 9} fontFamily="'DM Sans', sans-serif">{span.name}</text>}
                  </g>
                );
              })}
              <AxisLeft scale={yPrice} numTicks={4} tickFormat={(value) => `$${value}`} stroke={colors.grid} tickStroke="transparent" tickLabelProps={() => ({ ...axisText(fontSize), textAnchor: 'end', dx: -2, dy: 3 })} />
              <AxisBottom top={innerPriceHeight} scale={x} tickValues={ticks} tickLength={3} tickFormat={labelFor} stroke={colors.grid} tickStroke={colors.grid} tickLabelProps={() => ({ ...axisText(fontSize), textAnchor: 'middle', dy: -1 })} />
              {cursor}
              {overlay(innerPriceHeight)}
            </g>
          </svg>
          <svg width={size.width} height={occupancyHeight} role="img" aria-label="Your booked nights against market occupancy" style={{ marginTop: gap }}>
            <g transform={`translate(${margin.left},${margin.top})`}>
              <GridRows scale={yOccupancy} width={innerWidth} numTicks={2} stroke={colors.grid} strokeOpacity={0.7} />
              {rows.filter((row) => row.isBooked).map((row) => (
                <rect key={row.date} x={x(new Date(row.t)) - barWidth / 2} y={0} width={barWidth} height={innerOccupancyHeight} fill={colors.booked} fillOpacity={0.42} />
              ))}
              <LinePath data={rows} defined={(d) => d.marketOccupancy && Number.isFinite(d.marketOccupancy.lastYear)} x={(d) => x(new Date(d.t))} y={(d) => yOccupancy(d.marketOccupancy.lastYear)} curve={curveMonotoneX} stroke={colors.market} strokeOpacity={0.4} strokeWidth={1} strokeDasharray="3 3" />
              <LinePath data={rows} defined={(d) => d.marketOccupancy && Number.isFinite(d.marketOccupancy.market)} x={(d) => x(new Date(d.t))} y={(d) => yOccupancy(d.marketOccupancy.market)} curve={curveMonotoneX} stroke={colors.market} strokeWidth={compact ? 1.5 : 1.75} />
              <AxisLeft scale={yOccupancy} numTicks={2} tickFormat={(value) => `${value}%`} stroke={colors.grid} tickStroke="transparent" tickLabelProps={() => ({ ...axisText(fontSize), textAnchor: 'end', dx: -2, dy: 3 })} />
              <AxisBottom top={innerOccupancyHeight} scale={x} tickValues={ticks} tickLength={3} tickFormat={labelFor} stroke={colors.grid} tickStroke={colors.grid} tickLabelProps={() => ({ ...axisText(fontSize), textAnchor: 'middle', dy: -1 })} />
              {cursor}
              {overlay(innerOccupancyHeight)}
            </g>
          </svg>
          {showPosition && (
            <svg width={size.width} height={positionHeight} role="img" aria-label="Your price position within the market price range" style={{ marginTop: gap }}>
              <g transform={`translate(${margin.left},${margin.top})`}>
                <rect x={0} y={yPosition(100)} width={innerWidth} height={yPosition(75) - yPosition(100)} fill={colors.bandHigh} fillOpacity={0.16} />
                <rect x={0} y={yPosition(75)} width={innerWidth} height={yPosition(50) - yPosition(75)} fill={colors.bandMid} fillOpacity={0.3} />
                <rect x={0} y={yPosition(50)} width={innerWidth} height={yPosition(25) - yPosition(50)} fill={colors.bandLow} fillOpacity={0.26} />
                <GridRows scale={yPosition} tickValues={[25, 50, 75]} width={innerWidth} stroke={colors.grid} strokeOpacity={0.9} />
                <LinePath data={rows} defined={(d) => d.position !== null} x={(d) => x(new Date(d.t))} y={(d) => yPosition(d.position)} curve={curveMonotoneX} stroke={colors.position} strokeWidth={2} />
                <AxisLeft scale={yPosition} tickValues={[25, 50, 75]} tickFormat={(value) => ordinal(value)} stroke={colors.grid} tickStroke="transparent" tickLabelProps={() => ({ ...axisText(fontSize), textAnchor: 'end', dx: -2, dy: 3 })} />
                <AxisBottom top={innerPositionHeight} scale={x} tickValues={ticks} tickLength={3} tickFormat={labelFor} stroke={colors.grid} tickStroke={colors.grid} tickLabelProps={() => ({ ...axisText(fontSize), textAnchor: 'middle', dy: -1 })} />
                {cursor}
                {overlay(innerPositionHeight)}
              </g>
            </svg>
          )}
          {showPosition && (
            <div className="pb-position-summary">
              <strong><i className="swatch line" style={{ background: colors.position }} />Your position in the market{zones.average !== null ? `: ${ordinal(zones.average)} percentile on average` : ''}</strong>
              {zones.total > 0 && ['Below 25th', '25th to 50th', '50th to 75th', 'Above 75th'].map((label, index) => (
                <span className={`zone zone-${index}`} key={label}>{label}<b>{zones.counts[index]}</b><small>{Math.round((zones.counts[index] / zones.total) * 100)}%</small></span>
              ))}
              <small>open nights</small>
            </div>
          )}
          <div className="pb-legend pb-legend-foot">
            <span><i className="swatch band" style={{ background: colors.booked, opacity: 0.6 }} />Your booked nights</span>
            <span><i className="swatch line" style={{ background: colors.market }} />Market occupancy</span>
            <span><i className="swatch dash" style={{ borderColor: colors.market, opacity: 0.6 }} />Same time last year</span>
          </div>
        </>
      )}
      {hovered && (
        <div className="pb-tooltip" style={{ left: Math.min(Math.max(8, margin.left + x(new Date(hovered.t)) + 12), size.width - 190), top: 26 }}>
          <strong>{longDate(hovered.date)}</strong>
          <ul>
            <li><b style={{ color: colors.price }}>{dollars(hovered.price)}</b> your price{hovered.livePrice ? ` · ${dollars(hovered.livePrice)} live` : ''}</li>
            {hovered.market && <li>Market {dollars(hovered.market.p25)}–{dollars(hovered.market.p75)} <small>(90th {dollars(hovered.market.p90)})</small></li>}
            {hovered.market && Number.isFinite(hovered.market.medianBooked) && <li>Median booked {dollars(hovered.market.medianBooked)}</li>}
            <li>{hovered.isBooked ? 'Booked' : 'Open'}{hovered.demand ? <> · <span style={{ color: demandTone(hovered.demand) }}>{hovered.demand} demand</span></> : null}</li>
            {hovered.position !== null && <li>Position <b style={{ color: colors.position }}>{ordinal(hovered.position)} percentile</b></li>}
            {hovered.marketOccupancy && <li>Market occupancy {hovered.marketOccupancy.market}% <small>(last year {hovered.marketOccupancy.lastYear}%)</small></li>}
            {hovered.event && <li style={{ color: '#c4b5ff' }}>{hovered.event}</li>}
            {hovered.override && <li style={{ color: colors.override }}>Override {hovered.override.type === 'percent' ? `+${hovered.override.price}%` : dollars(hovered.override.price)}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// Market occupancy over the coming year against the same time last year, with your own weekly occupancy for the
// stretch PriceLabs has priced. Daily market occupancy is noisy, so it is drawn as a seven-day average.
export function OutlookChart({ pricing, bookedDates, rangeDays = 180, interactive = false }) {
  const [wrapRef, size] = useSize();
  const [hover, setHover] = useState(null);
  const rows = useMemo(() => {
    const market = pricing.marketOccupancy;
    if (!market) return [];
    const count = Math.min(rangeDays, market.dates.length);
    const mine = pricing.days.map((day) => (day.booked || bookedDates.has(day.date) ? 100 : 0));
    const mineByDate = new Map(pricing.days.map((day, index) => [day.date, movingAverage(mine, 7)[index]]));
    const current = movingAverage(market.occupancy, 7);
    const lastYear = movingAverage(market.sameTimeLastYear, 7);
    return market.dates.slice(0, count).map((date, index) => ({
      date, t: parseDay(date).getTime(), market: current[index], lastYear: lastYear[index], yours: mineByDate.get(date) ?? null,
    }));
  }, [pricing, bookedDates, rangeDays]);

  const margin = { top: 10, right: 10, bottom: 19, left: 34 };
  const innerWidth = Math.max(0, size.width - margin.left - margin.right);
  const svgHeight = Math.max(0, size.height - 18);
  const innerHeight = Math.max(0, svgHeight - margin.top - margin.bottom);
  const compact = size.width < 700;
  const fontSize = compact ? 8 : 10;
  const x = useMemo(() => scaleTime({
    domain: rows.length ? [new Date(rows[0].t), new Date(rows[rows.length - 1].t)] : [new Date(), new Date()],
    range: [0, innerWidth],
  }), [rows, innerWidth]);
  const y = scaleLinear({ domain: [0, 100], range: [innerHeight, 0] });
  const ticks = useMemo(() => monthTicks(rows), [rows]);
  const labelFor = useMemo(() => tickLabel(rows), [rows]);
  const hovered = interactive && hover !== null ? rows[hover] : null;

  return (
    <div className="pb-charts" ref={wrapRef}>
      <div className="pb-legend">
        <span><i className="swatch line" style={{ background: colors.market }} />Market occupancy</span>
        <span><i className="swatch dash" style={{ borderColor: colors.market, opacity: 0.6 }} />Same time last year</span>
        <span><i className="swatch line" style={{ background: colors.yours }} />Your occupancy</span>
        <span className="pb-legend-note">7-day averages</span>
      </div>
      {size.width > 0 && size.height > 0 && (
        <svg width={size.width} height={svgHeight} role="img" aria-label="Market occupancy outlook">
          <g transform={`translate(${margin.left},${margin.top})`}>
            <GridRows scale={y} width={innerWidth} numTicks={4} stroke={colors.grid} strokeOpacity={0.7} />
            <LinePath data={rows} defined={(d) => Number.isFinite(d.lastYear)} x={(d) => x(new Date(d.t))} y={(d) => y(d.lastYear)} curve={curveMonotoneX} stroke={colors.market} strokeOpacity={0.45} strokeWidth={1.25} strokeDasharray="4 3" />
            <LinePath data={rows} defined={(d) => Number.isFinite(d.market)} x={(d) => x(new Date(d.t))} y={(d) => y(d.market)} curve={curveMonotoneX} stroke={colors.market} strokeWidth={compact ? 1.75 : 2.25} />
            <LinePath data={rows} defined={(d) => Number.isFinite(d.yours)} x={(d) => x(new Date(d.t))} y={(d) => y(d.yours)} curve={curveMonotoneX} stroke={colors.yours} strokeWidth={compact ? 1.75 : 2.25} />
            <AxisLeft scale={y} numTicks={4} tickFormat={(value) => `${value}%`} stroke={colors.grid} tickStroke="transparent" tickLabelProps={() => ({ ...axisText(fontSize), textAnchor: 'end', dx: -2, dy: 3 })} />
            <AxisBottom top={innerHeight} scale={x} tickValues={ticks} tickLength={3} tickFormat={labelFor} stroke={colors.grid} tickStroke={colors.grid} tickLabelProps={() => ({ ...axisText(fontSize), textAnchor: 'middle', dy: -1 })} />
            {hovered && <line x1={x(new Date(hovered.t))} x2={x(new Date(hovered.t))} y1={0} y2={innerHeight} stroke={colors.axis} strokeDasharray="2 3" pointerEvents="none" />}
            {interactive && (
              <rect
                x={0} y={0} width={innerWidth} height={innerHeight} fill="transparent"
                onPointerMove={(event) => {
                  const box = event.currentTarget.getBoundingClientRect();
                  setHover(nearestIndex(rows, ((event.clientX - box.left) / box.width) * innerWidth, x));
                }}
                onPointerLeave={() => setHover(null)}
              />
            )}
          </g>
        </svg>
      )}
      {hovered && (
        <div className="pb-tooltip" style={{ left: Math.min(Math.max(8, margin.left + x(new Date(hovered.t)) + 12), size.width - 190), top: 26 }}>
          <strong>{longDate(hovered.date)}</strong>
          <ul>
            <li>Market {Number.isFinite(hovered.market) ? `${hovered.market.toFixed(0)}%` : '—'} <small>(last year {Number.isFinite(hovered.lastYear) ? `${hovered.lastYear.toFixed(0)}%` : '—'})</small></li>
            {Number.isFinite(hovered.yours) && <li style={{ color: colors.yours }}>Yours {hovered.yours.toFixed(0)}%</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// Monthly market bars (booking window, length of stay) for the outlook page.
export function TrendBars({ title, unit, months, values, highlight }) {
  const [wrapRef, size] = useSize();
  const data = months.map((month, index) => ({ month, value: values[index] })).filter((item) => Number.isFinite(item.value)).slice(-12);
  const margin = { top: 14, right: 4, bottom: 16, left: 4 };
  const innerWidth = Math.max(0, size.width - margin.left - margin.right);
  const innerHeight = Math.max(0, size.height - margin.top - margin.bottom);
  const max = Math.max(1, ...data.map((item) => item.value));
  const step = data.length ? innerWidth / data.length : 0;
  const compact = size.width < 260;
  return (
    <div className="pb-trend">
      <header><strong>{title}</strong>{Number.isFinite(highlight) && <span>{highlight}<small> {unit} · last 365 days</small></span>}</header>
      <div className="pb-trend-plot" ref={wrapRef}>
        {size.width > 0 && size.height > 0 && (
          <svg width={size.width} height={size.height} role="img" aria-label={title}>
            <g transform={`translate(${margin.left},${margin.top})`}>
              {data.map((item, index) => {
                const height = (item.value / max) * innerHeight;
                const left = index * step + step * 0.18;
                return (
                  <g key={item.month}>
                    <rect x={left} y={innerHeight - height} width={step * 0.64} height={height} rx={2} fill={colors.yours} fillOpacity={index === data.length - 1 ? 0.95 : 0.55} />
                    <text x={left + step * 0.32} y={innerHeight - height - 3} textAnchor="middle" fill="#c9d4e6" fontSize={compact ? 7 : 9} fontFamily="'DM Sans', sans-serif">{item.value}</text>
                    {(!compact || index % 2 === 0) && <text x={left + step * 0.32} y={innerHeight + 11} textAnchor="middle" fill={colors.axis} fontSize={compact ? 7 : 9} fontFamily="'DM Sans', sans-serif">{item.month.slice(0, 3)}</text>}
                  </g>
                );
              })}
            </g>
          </svg>
        )}
      </div>
    </div>
  );
}

